import { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box, Text, Flex, Badge, Heading,
  Card, CardBody, CardHeader, useColorModeValue,
  HStack, VStack, Spinner, Button, Select, useToast, Tooltip,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody,
  ModalFooter, ModalCloseButton, useDisclosure,
} from "@chakra-ui/react";
import { MdRefresh, MdPlayArrow,MdDownload } from "react-icons/md";
import { TbChartBar } from "react-icons/tb";
import apiService from "../services/apiService";
import * as XLSX from "xlsx";

// ── Widths & sticky offsets ───────────────────────────────────────────────────
const W = { client: 210, col: 190 };
const L = { client: 0 };

// ── Helpers ───────────────────────────────────────────────────────────────────
const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

const isDateLike = v =>
  typeof v === "string" && /^\d{4}-\d{2}-\d{2}/.test(v);

const fmtDate = d => {
  if (!d) return "—";
  try {
    const [datePart, timePart] = d.split("T");
    if (!datePart) return "—";
    const [y, m, day] = datePart.split("-").map(Number);
    if (!y || !m || !day || y < 1950) return "N/A";
    const dateStr = `${MONTHS[m - 1]} ${day}, ${y}`;
    if (!timePart) return dateStr;
    const cleanTime = timePart.replace("Z", "").split(".")[0];
    const [h, min] = cleanTime.split(":").map(Number);
    const ampm = h >= 12 ? "PM" : "AM";
    const hour = h % 12 || 12;
    const mins = String(min).padStart(2, "0");
    return `${dateStr}, ${hour}:${mins} ${ampm}`;
  } catch { return "—"; }
};

const fmtExec = d => {
  if (!d) return "—";
  try {
    const dt = new Date(d.replace(" ", "T").split(".")[0] + "Z");
    if (isNaN(dt)) return "—";
    return dt.toLocaleString("en-US", {
      month: "2-digit", day: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit", hour12: true,
    });
  } catch { return "—"; }
};

const isStale = d => {
  if (!d) return false;
  try {
    const dt = new Date(d.replace(" ", "T").split(".")[0] + "Z");
    const cutoff = new Date();
    cutoff.setMonth(cutoff.getMonth() - 2);
    return dt < cutoff;
  } catch { return false; }
};

// snake_case / camelCase → "Title Case"
const fmtKey = key =>
  key.replace(/_/g, " ").replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, c => c.toUpperCase());

// Cell formatter: show raw value as-is
const fmtCell = val => {
  if (val === null || val === undefined) return null;
  return String(val);
};





// ── Main ──────────────────────────────────────────────────────────────────────
export default function DynamicReportResults() {
  const [reports,         setReports]         = useState([]);  // [{reportId, reportName}]
  const [selectedReport,  setSelectedReport]  = useState("");
  const [data,            setData]            = useState([]);
  const [reportTitle,     setReportTitle]     = useState("");
  const [reportsLoading,  setReportsLoading]  = useState(true);
  const [loading,         setLoading]         = useState(false);
  const [error,           setError]           = useState(null);
  const [reloading,       setReloading]       = useState(false);

  const { isOpen: isReloadOpen, onOpen: openReload, onClose: closeReload } = useDisclosure();
  const toast   = useToast();
  const bg      = useColorModeValue("white",   "gray.800");
  const border  = useColorModeValue("#e2e8f0", "#2d3748");
  const headClr = useColorModeValue("#1a202c", "white");
  const subClr  = useColorModeValue("#4a5568", "#cbd5e0");
  const mutedClr= useColorModeValue("#718096", "#a0aec0");
  const rowEven = useColorModeValue("#ffffff", "#1a202c");
  const rowOdd  = useColorModeValue("#f7fafc", "#171923");
  const hdrBg   = "#1a3a6b";
  const subHdrBg= "#22508a";
  const thinB   = `1px solid ${border}`;
  const thickB  = "2px solid #94a3b8";

  // ── Load report list on mount ─────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const res = await apiService.fetchDynamicReports();
        if (res.success && Array.isArray(res.data)) setReports(res.data);
      } catch { /* silently fail – user can still type */ }
      finally { setReportsLoading(false); }
    })();
  }, []);

  // ── Fetch results for selected report ────────────────────────────────────
  const fetchResults = useCallback(async (reportId) => {
    if (!reportId) return;
    setLoading(true); setError(null); setData([]);
    try {
      const res = await apiService.fetchDynamicReportResults(reportId);
      if (res.success && Array.isArray(res.data)) {
        setData(res.data);
        if (res.data.length > 0) setReportTitle(res.data[0].reportName || "");
      } else {
        setError(res.message || "Failed to load report results");
      }
    } catch { setError("Failed to load report results"); }
    finally   { setLoading(false); }
  }, []);

  const handleReportChange = e => {
    const id = e.target.value;
    setSelectedReport(id);
    if (!id) {
      setData([]);
      setReportTitle("");
      setError(null);
      return;
    }
    fetchResults(id);
  };

  const handleRefresh = () => fetchResults(selectedReport);

  const handleReloadConfirm = async () => {
    closeReload();
    setReloading(true);
    toast({
      title: "Reload Queued",
      description: `Results for "${reportTitle}" will be re-executed and ready within 2 mins.`,
      status: "info",
      duration: 5000,
      isClosable: true,
      position: "top-right",
    });
    try {
      const res = await apiService.reloadDynamicReportResultsbyReport(selectedReport);
      if (!res.success) {
        toast({
          title: "Reload Failed",
          description: res.message || "Failed to queue reload. Please try again.",
          status: "error",
          duration: 5000,
          isClosable: true,
          position: "top-right",
        });
      }
    } catch {
      toast({
        title: "Reload Error",
        description: "An unexpected error occurred while queuing the reload.",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    } finally {
      setReloading(false);
    }
  };

  // ── Derive columns in the report's SELECT query order ───────────────────
  // Prefer the server-provided column_names (query order); fall back to row keys.
  const columns = useMemo(() => {
    const seen = new Set();
    const cols = [];
    data.forEach(({ column_names = [] }) => {
      column_names.forEach(k => {
        if (!seen.has(k)) { seen.add(k); cols.push(k); }
      });
    });
    // Append any keys present in the data but not listed in column_names (defensive)
    data.forEach(({ report_data = [] }) => {
      report_data.forEach(row => {
        Object.keys(row).forEach(k => {
          if (!seen.has(k)) { seen.add(k); cols.push(k); }
        });
      });
    });
    return cols;
  }, [data]);

  // ── Sort clients: most total rows first ──────────────────────────────────
  const sortedData = useMemo(() =>
    [...data].sort((a, b) => (b.report_data?.length || 0) - (a.report_data?.length || 0)),
  [data]);

  const totalRows = useMemo(() =>
    data.reduce((acc, c) => acc + (c.report_data?.length || 1), 0),
  [data]);

  // ── Sticky style helpers ──────────────────────────────────────────────────
  const sHead = (left, w, extra = {}) => ({
    position: "sticky", left, top: 0, zIndex: 12,
    background: hdrBg, color: "white",
    width: w, minWidth: w, maxWidth: w,
    padding: "10px 12px", fontSize: "11px", fontWeight: "700",
    whiteSpace: "nowrap", textAlign: "center",
    borderRight: thinB, boxSizing: "border-box", ...extra,
  });

  const sCell = (left, w, rb, rs, extra = {}) => ({
    position: "sticky", left, zIndex: 4,
    background: rb, width: w, minWidth: w,
    padding: "7px 12px", borderRight: thickB,
    boxSizing: "border-box", verticalAlign: "top",
    borderBottom: thinB, ...extra,
  });

  const dHead = {
    position: "sticky", top: 0, zIndex: 6,
    background: hdrBg, color: "white",
    padding: "10px 12px", fontSize: "11px", fontWeight: "700",
    textAlign: "center", whiteSpace: "nowrap",
    borderRight: thinB, boxSizing: "border-box",
    width: W.col, minWidth: W.col,
  };

  const handleDownloadExcel = useCallback(() => {
  if (!data.length || !columns.length) return;

  // Build flat rows: one row per report_data entry (or one "No data" row per client)
  const rows = [];
  sortedData.forEach(client => {
    const base = {
      Client:        client.clientName  || "",
      "Last Executed": fmtExec(client.last_executed),
    };

    if (client.report_data?.length > 0) {
      client.report_data.forEach(rd => {
        const row = { ...base };
        columns.forEach(col => {
          // Preserve numbers as numbers so Excel can sort/sum them
          const v = rd[col];
          row[fmtKey(col)] =
            v === null || v === undefined ? ""
            : typeof v === "number"      ? v
            : isDateLike(String(v))      ? fmtDate(String(v))   // human-readable date
            : String(v);
        });
        rows.push(row);
      });
    } else {
      // Client exists but has no report rows – still include one blank row
      const row = { ...base };
      columns.forEach(col => { row[fmtKey(col)] = ""; });
      rows.push(row);
    }
  });

  const ws = XLSX.utils.json_to_sheet(rows);

  // Auto-width: measure each column by the longest cell value
  const colKeys  = Object.keys(rows[0] || {});
  const colWidths = colKeys.map(k => ({
    wch: Math.min(
      40,
      Math.max(
        k.length,
        ...rows.map(r => String(r[k] ?? "").length)
      )
    ),
  }));
  ws["!cols"] = colWidths;

  // Freeze the first two header rows (just the header row here) + first column
  ws["!freeze"] = { xSplit: 1, ySplit: 1, topLeftCell: "B2", activePane: "bottomRight" };

  const wb = XLSX.utils.book_new();
  // Excel sheet names must be <= 31 chars and exclude : \ / ? * [ ]
  const safeSheetName =
    (reportTitle || "Report Results")
      .replace(/[\\/?*[\]:]/g, " ")
      .trim()
      .slice(0, 31) || "Report Results";
  XLSX.utils.book_append_sheet(wb, ws, safeSheetName);

  const fileName = `${(reportTitle || "report").replace(/\s+/g, "_")}_results.xlsx`;
  XLSX.writeFile(wb, fileName);
}, [data, sortedData, columns, reportTitle]);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Box p={4}>
      <VStack spacing={5} align="stretch">

        {/* Page Header */}
        <Flex justify="space-between" align="center">
          <Box>
            <Heading size="lg" color={headClr}>Dynamic Report Results</Heading>
            <Text color={subClr} mt={1} fontSize="sm">
              Select a report to view its latest execution results across all mapped clients
            </Text>
          </Box>
          <HStack>
            <Tooltip label="Download current results as an Excel spreadsheet" placement="bottom">
                <Button
                    leftIcon={<MdDownload />}         
                    onClick={handleDownloadExcel}
                    colorScheme="green"
                    variant="outline"
                    size="sm"
                    isDisabled={!selectedReport || loading || data.length === 0}
                >
                  Download Report
              </Button>
          </Tooltip>

            <Tooltip label="Re-executes the report and refreshes data — results ready in ~2 mins" placement="bottom">
              <Button
                leftIcon={<MdPlayArrow />}
                onClick={openReload}
                colorScheme="orange"
                variant="outline"
                size="sm"
                isDisabled={!selectedReport || loading}
                isLoading={reloading}
                loadingText="Queuing…"
              >
                Re-run Report
              </Button>
            </Tooltip>
            <Tooltip label="Fetches the latest already-stored results from the database" placement="bottom">
              <Button
                leftIcon={<MdRefresh />}
                onClick={handleRefresh}
                variant="outline"
                size="sm"
                isDisabled={!selectedReport}
                isLoading={loading}
              >
                Refresh Data
              </Button>
            </Tooltip>
          </HStack>

          {/* Reload Confirmation Modal */}
          <Modal isOpen={isReloadOpen} onClose={closeReload} isCentered size="md">
            <ModalOverlay />
            <ModalContent>
              <ModalHeader fontSize="md">Reload Report Results</ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <Text fontSize="sm" color={subClr}>
                  Are you sure you want to reload results for{" "}
                  <Text as="span" fontWeight="700" color={headClr}>
                    {reportTitle || "this report"}
                  </Text>
                  ?
                </Text>
                <Text fontSize="sm" color={mutedClr} mt={2}>
                  The report will be added to the execution queue and results will be ready within <strong>2 minutes</strong>.
                </Text>
              </ModalBody>
              <ModalFooter gap={2}>
                <Button size="sm" variant="ghost" onClick={closeReload}>Cancel</Button>
                <Button size="sm" colorScheme="orange" onClick={handleReloadConfirm}>
                  Yes, Reload
                </Button>
              </ModalFooter>
            </ModalContent>
          </Modal>
        </Flex>

        {/* Report Selector */}
        <Card bg={bg} borderColor={border} borderWidth="1px">
          <CardBody py={4} px={5}>
            <Flex align="center" gap={4} wrap="wrap">
              <Box flex="1" minW="220px" maxW="400px">
                <Text fontSize="xs" fontWeight="600" color={mutedClr} mb={1} textTransform="uppercase" letterSpacing="0.06em">
                  Select Report
                </Text>
                <Select
                  placeholder={reportsLoading ? "Loading reports…" : "Choose a report…"}
                  value={selectedReport}
                  onChange={handleReportChange}
                  isDisabled={reportsLoading}
                  size="sm"
                  borderColor={border}
                  focusBorderColor="blue.400"
                >
                  {reports.map(r => (
                    <option key={r.id} value={r.id}>{r.name}</option>
                  ))}
                </Select>
              </Box>
              {selectedReport && reportTitle && (
                <Box>
                  <Text fontSize="xs" fontWeight="600" color={mutedClr} mb={1} textTransform="uppercase" letterSpacing="0.06em">
                    Report
                  </Text>
                  <Badge colorScheme="blue" variant="subtle" fontSize="sm" px={3} py={1}>
                    {reportTitle}
                  </Badge>
                </Box>
              )}
              {!loading && data.length > 0 && (
                <Box>
                  <Text fontSize="xs" fontWeight="600" color={mutedClr} mb={1} textTransform="uppercase" letterSpacing="0.06em">
                    Clients
                  </Text>
                  <Badge colorScheme="purple" variant="subtle" fontSize="sm" px={3} py={1}>
                    {data.length}
                  </Badge>
                </Box>
              )}
            </Flex>
          </CardBody>
        </Card>

        {/* Prompt – no report selected */}
        {!selectedReport && !loading && (
          <Flex justify="center" py={16}>
            <VStack spacing={3}>
              <Box color={mutedClr}><TbChartBar size={44} /></Box>
              <Text color={mutedClr} fontSize="lg">Select a report above to load results</Text>
            </VStack>
          </Flex>
        )}

        {/* Loading */}
        {loading && (
          <Flex justify="center" py={16}>
            <VStack spacing={4}>
              <Spinner size="lg" color="blue.500" thickness="3px" />
              <Text color={subClr}>Loading report results…</Text>
            </VStack>
          </Flex>
        )}

        {/* Error */}
        {!loading && error && (
          <Flex justify="center" py={16}>
            <VStack spacing={3}>
              <Box color="red.400"><TbChartBar size={40} /></Box>
              <Text color="red.500" fontWeight="semibold">{error}</Text>
              <Button size="sm" onClick={handleRefresh} colorScheme="blue" variant="outline">Retry</Button>
            </VStack>
          </Flex>
        )}

        {/* Empty */}
        {!loading && !error && selectedReport && data.length === 0 && (
          <Flex justify="center" py={16}>
            <VStack spacing={3}>
              <Box color={mutedClr}><TbChartBar size={44} /></Box>
              <Text color={mutedClr} fontSize="lg">No results found for this report</Text>
            </VStack>
          </Flex>
        )}

        {/* ── Table ─────────────────────────────────────────────────────────── */}
        {!loading && !error && data.length > 0 && (
          <Card bg={bg} borderColor={border} borderWidth="1px" overflow="hidden">
            <CardHeader pb={2} pt={4} px={4}>
              <Flex justify="space-between" align="center">
                <HStack spacing={3}>
                  <Box color="blue.500"><TbChartBar size={20} /></Box>
                  <Heading size="md" color={headClr}>
                    {reportTitle || "Execution Results"}
                  </Heading>
                </HStack>
                <Badge colorScheme="blue" variant="subtle" fontSize="sm" px={3} py={1}>
                  {data.length} client{data.length !== 1 ? "s" : ""}
                </Badge>
              </Flex>
            </CardHeader>

            <CardBody p={0}>
              <Box
                overflowX="auto" overflowY="auto" maxH="72vh"
                sx={{
                  "&::-webkit-scrollbar": { h: "8px", w: "8px" },
                  "&::-webkit-scrollbar-thumb": { bg: "gray.300", borderRadius: "4px" },
                }}
              >
                <table style={{
                  borderCollapse: "collapse", tableLayout: "fixed",
                  minWidth: `${W.client + columns.length * W.col}px`,
                }}>
                  <thead>
                    <tr>
                      {/* Sticky client header */}
                      <th style={sHead(L.client, W.client, { textAlign: "left", borderRight: thickB })}>
                        Client
                      </th>
                      {/* Dynamic column headers */}
                      {columns.map((col, i) => (
                        <th key={col} style={{ ...dHead, borderLeft: i === 0 ? "none" : thinB }}>
                          {fmtKey(col)}
                        </th>
                      ))}
                    </tr>
                  </thead>

                  <tbody>
                    {sortedData.map((client, cidx) => {
                      const rb       = cidx % 2 === 0 ? rowEven : rowOdd;
                      const stale    = isStale(client.last_executed);
                      const rows     = client.report_data?.length > 0 ? client.report_data : [null];
                      const rowCount = rows.length;

                      return rows.map((row, rowIdx) => (
                        <tr key={`${client.clientId}-${rowIdx}`}>

                          {/* ── Sticky Client cell – only on first row ─── */}
                          {rowIdx === 0 && (
                            <td
                              rowSpan={rowCount}
                              style={sCell(L.client, W.client, rb, rowCount, {
                                borderBottom: `2px solid #94a3b8`,
                              })}
                            >
                              <div style={{ fontWeight: 700, fontSize: 13, color: headClr }}>
                                {client.clientName}
                              </div>
                              <Tooltip label="Last executed">
                                <div style={{
                                  fontSize: 11, marginTop: 4, display: "inline-block",
                                  borderRadius: 3, padding: stale ? "1px 5px" : 0,
                                  color: stale ? "#e53e3e" : mutedClr,
                                  background: stale ? "#fff5f5" : "transparent",
                                }}>
                                  {fmtExec(client.last_executed)}
                                </div>
                              </Tooltip>
                              {rowCount > 1 && null}
                            </td>
                          )}

                          {/* ── Dynamic data cells ───────────────────────── */}
                          {columns.map((col, i) => {
                            const raw      = row ? row[col] : undefined;
                            const rendered = fmtCell(raw);
                            const isLast   = rowIdx === rowCount - 1;

                            return (
                              <td key={col} style={{
                                width: W.col, minWidth: W.col,
                                padding: "7px 12px", fontSize: 12,
                                color: rendered ? subClr : mutedClr,
                                textAlign: "center", background: rb,
                                borderLeft: i === 0 ? thickB : thinB,
                                borderBottom: isLast ? "2px solid #94a3b8" : thinB,
                                boxSizing: "border-box",
                                verticalAlign: "middle",
                              }}>
                                {row === null ? (
                                  <span style={{ color: mutedClr, fontStyle: "italic", fontSize: 11 }}>No data</span>
                                ) : rendered !== null ? (
                                  rendered
                                ) : (
                                  <span style={{ color: mutedClr, fontSize: 13 }}>—</span>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ));
                    })}
                  </tbody>
                </table>
              </Box>
            </CardBody>
          </Card>
        )}
      </VStack>
    </Box>
  );
}